import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { adminLogin, clientLogin, clientSignup, photographerSignup, getMe, logoutSession, submitMfaChallenge } from '@/lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // { role, email, name, gallery_slugs }
  const [loading, setLoading] = useState(true);

  // The session lives in an httpOnly cookie; /auth/me tells us whether it is valid.
  const loadSession = useCallback(async () => {
    try {
      const me = await getMe();
      setUser(me);
    } catch (err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const persistSession = (data) => {
    // Cookie is already set by the backend; only keep the public profile in memory.
    const { access_token: _token, token_type: _type, ...profile } = data;
    setUser(profile);
  };

  // Login calls may return either a full session payload, or
  // `{ mfa_required: true, challenge_token }` when the account has 2FA
  // enabled — in that case no cookie is set yet, so we must NOT persist a
  // session until the challenge is resolved.
  const loginAsAdmin = async (email, password) => {
    const data = await adminLogin(email, password);
    if (!data.mfa_required) persistSession(data);
    return data;
  };

  const loginAsClient = async (email, password) => {
    const data = await clientLogin(email, password);
    if (!data.mfa_required) persistSession(data);
    return data;
  };

  const resolveMfaChallenge = async (challengeToken, code) => {
    const data = await submitMfaChallenge(challengeToken, code);
    persistSession(data);
    return data;
  };

  const signupAsClient = async (email, password, name) => {
    const data = await clientSignup(email, password, name);
    persistSession(data);
    return data;
  };

  const signupAsPhotographer = async (fields) => {
    const data = await photographerSignup(fields);
    persistSession(data);
    return data;
  };

  const logout = async () => {
    setUser(null);
    try {
      await logoutSession();
    } catch {
      // Cookie clearing failed (offline?) — the in-memory session is already gone.
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAdmin: user?.role === 'admin' || user?.role === 'photographer',
        isClient: user?.role === 'client',
        loginAsAdmin,
        loginAsClient,
        resolveMfaChallenge,
        signupAsClient,
        signupAsPhotographer,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
